import {
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useFocusTrap } from "../../../utils/useFocusTrap";
import { MIN_VISIBLE_ITEMS, POPUP_GAP, VIEWPORT_MARGIN } from "./constants";
import {
  getPopupChromeHeight,
  getPopupHeight,
  getPopupMinWidth,
} from "./popupGeometry";
import { clamp } from "./timeValueHelper";
import { PopupLayout } from "./types";

interface UseTimePickerPopupParams {
  disabled: boolean;
  segmentCount: number;
  itemHeight: number;
  columnWidth: number;
  /** Preferred amount of visible options, reduced when the viewport is short. */
  visibleItems: number;
  withActions: boolean;
  /** Element the popup is positioned against (the input row). */
  anchorRef: RefObject<HTMLElement>;
  /** Clicks/scrolls inside the picker itself do not close the popup. */
  containerRef: RefObject<HTMLElement>;
  inputRef: RefObject<HTMLInputElement>;
  onOpenChange?: (isOpen: boolean) => void;
}

interface UseTimePickerPopupResult {
  isOpen: boolean;
  /** Measured before the popup mounts, so it never renders unpositioned. */
  layout: PopupLayout | null;
  /** `withFocus` moves focus into the popup, for keyboard interactions. */
  openPopup: (withFocus?: boolean) => void;
  /**
   * `restoreFocus` defaults to "only if the popup is holding focus". Pass false
   * when closing because focus already moved on, so it is not dragged back.
   */
  closePopup: (restoreFocus?: boolean) => void;
  /** True when this open asked for focus, handed to the popup as `autoFocus`. */
  autoFocusPopup: boolean;
  /** Attach to the popup element, it doubles as the focus trap container. */
  popupRef: MutableRefObject<HTMLDivElement | null>;
}

/**
 * Owns the dropdown: open state, viewport aware position/size, closing on
 * interaction outside of the picker, and focus.
 *
 * Focus containment is delegated to the shared `useFocusTrap`, the same helper
 * the table filter popups use: it keeps Tab cycling inside the popup, remembers
 * what opened it and hands focus back on close. That matters here because the
 * popup is portaled outside of the picker, so it is unreachable by tabbing.
 */
export const useTimePickerPopup = ({
  disabled,
  segmentCount,
  itemHeight,
  columnWidth,
  visibleItems,
  withActions,
  anchorRef,
  containerRef,
  inputRef,
  onOpenChange,
}: UseTimePickerPopupParams): UseTimePickerPopupResult => {
  const [isOpen, setIsOpen] = useState(false);
  const [layout, setLayout] = useState<PopupLayout | null>(null);
  const [autoFocusPopup, setAutoFocusPopup] = useState(false);

  // `true` keeps focus() from scrolling the option columns out of alignment
  const focusTrap = useFocusTrap(true);
  const { containerRef: popupRef, triggerRef } = focusTrap;
  /*
   * useFocusTrap returns fresh function identities on every render, but they
   * only close over its own stable refs. Pin them so our callbacks and the
   * document listeners below do not have to be rebuilt on each render. Written
   * after the commit, since a render must not have side effects.
   */
  const focusTrapRef = useRef(focusTrap);
  /* Same for the callback prop: keeping it out of the deps below leaves
     openPopup/closePopup stable when the parent passes an inline function. */
  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => {
    focusTrapRef.current = focusTrap;
    onOpenChangeRef.current = onOpenChange;
  });

  const measureLayout = useCallback((): PopupLayout | null => {
    const anchor = anchorRef.current;
    if (!anchor) return null;

    const rect = anchor.getBoundingClientRect();
    const { innerHeight, innerWidth, scrollX, scrollY } = window;

    const chromeHeight = getPopupChromeHeight(withActions);
    const spaceBelow = innerHeight - rect.bottom - POPUP_GAP - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - POPUP_GAP - VIEWPORT_MARGIN;

    // shrink the columns until the popup fits the viewport, they stay scrollable
    const fittingItems = Math.floor(
      (Math.max(spaceBelow, spaceAbove) - chromeHeight) / itemHeight,
    );
    const nextVisibleItems = Math.max(
      MIN_VISIBLE_ITEMS,
      Math.min(
        visibleItems,
        Number.isFinite(fittingItems) ? fittingItems : visibleItems,
      ),
    );

    const popupHeight = getPopupHeight(
      nextVisibleItems,
      itemHeight,
      withActions,
    );
    const openAbove = spaceBelow < popupHeight && spaceAbove > spaceBelow;

    const minWidth = getPopupMinWidth(segmentCount, columnWidth);
    const popupWidth = clamp(
      Math.max(minWidth, rect.width),
      0,
      Math.max(minWidth, innerWidth - VIEWPORT_MARGIN * 2),
    );

    const minLeft = scrollX + VIEWPORT_MARGIN;
    const maxLeft = Math.max(
      minLeft,
      scrollX + innerWidth - popupWidth - VIEWPORT_MARGIN,
    );

    return {
      top: Math.max(
        scrollY + VIEWPORT_MARGIN,
        openAbove
          ? rect.top + scrollY - popupHeight - POPUP_GAP
          : rect.bottom + scrollY + POPUP_GAP,
      ),
      left: clamp(rect.left + scrollX, minLeft, maxLeft),
      width: popupWidth,
      visibleItems: nextVisibleItems,
    };
  }, [
    anchorRef,
    columnWidth,
    itemHeight,
    segmentCount,
    visibleItems,
    withActions,
  ]);

  const openPopup = useCallback(
    (withFocus = false) => {
      if (disabled) return;

      if (isOpen) {
        // already open, the caller focuses the popup itself if it wants to
        if (withFocus) setAutoFocusPopup(true);
        return;
      }

      /*
       * Measure before mounting. Rendering the popup unpositioned and hiding it
       * for a frame would make every option compute to visibility:hidden, and
       * the focus trap skips hidden elements - focus would go nowhere.
       */
      setLayout(measureLayout());
      // remembered for the focus trap, which restores it on close
      triggerRef.current = (document.activeElement as HTMLElement) ?? null;
      setAutoFocusPopup(withFocus);
      setIsOpen(true);
      onOpenChangeRef.current?.(true);
    },
    [disabled, isOpen, measureLayout, triggerRef],
  );

  const closePopup = useCallback(
    (restoreFocus?: boolean) => {
      if (!isOpen) return;

      /*
       * Restoring is right when the popup itself holds focus (Escape, Done, an
       * option confirmed): the focused element is about to disappear. It is
       * wrong when the popup closes *because* focus moved on - tabbing to the
       * next field would be yanked straight back.
       */
      const shouldRestore =
        restoreFocus ??
        (!!document.activeElement &&
          !!popupRef.current?.contains(document.activeElement));

      setIsOpen(false);
      setAutoFocusPopup(false);
      onOpenChangeRef.current?.(false);

      if (shouldRestore) focusTrapRef.current.onHide();
      else triggerRef.current = null;
    },
    [isOpen, popupRef, triggerRef],
  );

  // a pointer open leaves focus on the input, so that is where it returns to
  useEffect(() => {
    if (!isOpen || autoFocusPopup) return;
    triggerRef.current ??= inputRef.current;
  }, [isOpen, autoFocusPopup, inputRef, triggerRef]);

  // keep the popup aligned while it is open
  useLayoutEffect(() => {
    if (!isOpen) {
      setLayout(null);
      return undefined;
    }

    setLayout(measureLayout());

    const handleResize = () => setLayout(measureLayout());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isOpen, measureLayout]);

  // close on interaction outside of the input and the popup
  useEffect(() => {
    if (!isOpen) return undefined;

    const isInside = (target: EventTarget | null) => {
      if (!(target instanceof Node)) return false;
      return (
        !!containerRef.current?.contains(target) ||
        !!popupRef.current?.contains(target)
      );
    };

    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      if (!isInside(e.target)) closePopup();
    };
    // capture phase, scroll events do not bubble
    const handleScroll = (e: Event) => {
      if (!isInside(e.target)) closePopup();
    };

    // passive: neither handler prevents the gesture, they only close the popup
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, {
      passive: true,
    });
    document.addEventListener("scroll", handleScroll, {
      capture: true,
      passive: true,
    });
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen, closePopup, containerRef, popupRef]);

  return {
    isOpen,
    layout,
    openPopup,
    closePopup,
    autoFocusPopup,
    popupRef,
  };
};

export default useTimePickerPopup;
